import { TermStepEnum } from "../enums/termStep.enum";

import { AccountContactType } from "@/components/Contracts/ConfirmationStep/Signature/signature.interface";
import {
  Account,
  ApprovalFlow
} from "@/components/Contracts/contract.interface";
import { AccountType } from "@/modules/Accounts/types";
import { ContractStatus, SignatureType } from "@/modules/Contracts/enums";

export interface IContact {
  contact: string;
  contactType: AccountContactType;
  id: number;
  objectTypeCode: string;
  parentTypeCodeId: number;
}

export interface IStep {
  friendlyName: string;
  id: number;
  interaction: true;
  interactionAction?: string;
  interactionName?: string;
  interactionType?: string;
  internalName: ContractStatus;
  order: number;
  status: boolean;
}

interface IContractModel {
  code: string;
  content?: unknown;
  id: number;
  name: string;
  status: boolean;
}

interface ISignature {
  auth: string;
  authConfigs: {
    liveness_enabled: boolean;
  };
  contactType: string;
  id: number;
  name: string;
  notifyPath: string;
  status: boolean;
  type: SignatureType;
}

interface IUser {
  birthDate: string;
  businessUnitId: number;
  documentNumber?: string;
  email: string;
  fullName: string;
  id: number;
  status: true;
}

interface IEvent {
  data?: unknown;
  name: string;
  ocurredAt: Date;
}

interface IEletronicSignatureDocument {
  account_key: string;
  attachments: unknown[];
  auto_close: boolean;
  block_after_refusal: boolean;
  deadline_at: Date;
  downloads: { original_file_url: string };
  events: IEvent[];
  filename: string;
  finished_at: Date;
  key: string;
  links: { self: string };
  locale: string;
  metadata: unknown;
  path: string;
  remind_interval?: unknown;
  sequence_enabled: boolean;
  signable_group?: unknown;
  signers: ISigners[];
  status: string;
  template?: unknown;
  signature?: string;
  updateAt: Date;
  createdAt: Date;
}

export interface IDocumentEletronicSignature {
  document: IEletronicSignatureDocument;
  event: IEvent;
}

interface ISigners {
  list_key: string;
  key: string;
  request_signature_key: string;
  email: string;
  name: string;
  has_documentation: boolean;
  documentation: string;
  birthday: string;
  phone_number?: string;
  sign_as: SignerType;
  delivery: DeliveryType;
  url: string;
  auths: SignStrategyType[];
  created_at: Date;
  signature?: ISignatureType;
}

enum SignerType {
  /** @deprecated */
  TRANSFEROR = "transferor",
  /** @deprecated */
  TRANSFEREE = "transferee",
  /** @deprecated */
  DIRECTOR = "director",

  GUARANTOR = "guarantor",
  SIGN = "sign",

  LEGAL_REPRESENTATIVE = "legal_representative",
  ASSOCIATE = "associate"
}

export const LEGAL_REPRESENTATIVE_TYPES = [
  SignerType.LEGAL_REPRESENTATIVE,
  SignerType.TRANSFEROR, // deprecated
  SignerType.DIRECTOR // deprecated
];

enum DeliveryType {
  WHATSAPP = "whatsapp",
  EMAIL = "email",
  SMS = "sms"
}

export enum SignStrategyType {
  API = "api",
  EMAIL = "email",
  WHATSAPP = "whatsapp"
}

interface ISignatureType {
  name: string;
  email: string;
  birthday: string;
  documentation: string;
  validation: {
    status: string;
    name: string;
  };
  ip_address: string;
  signed_at: Date;
  sign_as: SignerType;
  official_document_front?: IDocumentFile;
  official_document_back?: IDocumentFile;
  liveness?: IDocumentFile;
}

interface IDocumentFile {
  name: string;
  original_url: string;
}

export interface ITerm {
  account: Account;
  approvalFlow: ApprovalFlow;
  contractModel: IContractModel;
  createdAt: Date;
  externalElectronicSignatureData?: IDocumentEletronicSignature;
  currentStep: IStep;
  id: number;
  nextStep: IStep;
  signature: ISignature;
  updatedAt: Date;
  user: IUser;
  businessUnitId: number;
  termType?: TermTypeEnum;
  termDataDraft?: ITermDataDraft;
  contractId?: number;
  pdfPath?: string;
}

export interface ITermDataDraft {
  id: number;
  termType: TermTypeEnum;
  fullName: string;
  accountType: AccountType;
  documentNumber: string;
  zipCode: string;
  city: string;
  uf: string;
  street: string;
  neighborhood: string;
  number: string;
  contractId: number;
  affiliation?: string;
  whatsapp?: string;
  email?: string;
  secondaryDocument?: string;
  legalRepresentativeName?: string;
  legalRepresentativeDocument?: string;
  destinationAccountNumber?: string;
  mirrorLineNumber?: string;
  signatureId?: number;
  serviceProviderDocumentId: number;
  note: string;
}

export enum TermTypeEnum {
  ASSOCIATION = "ASSOCIATION",
  CHANGE_OF_OWNERSHIP = "CHANGE_OF_OWNERSHIP",
  ASSOCIATION_AND_PROXY = "ASSOCIATION_AND_PROXY"
}
